import * as React from "react"
import { Transition } from 'react-transition-group'
import {Snackbar} from '@mui/base/Snackbar'
import cx from 'classnames'
import InformationIcon from "../images/svg/info.svg"
import CloseIcon from "../images/svg/close.svg"
import ExclamationIcon from "../images/svg/exclamation.svg"
import CheckIcon from "../images/svg/check.svg"

export const NotificationStatus = {
  INFORMATION: 'information',
  SUCCESS:'success',
  ERROR: 'error',
}


const Icon = ({ status, className, ...props }) => {
  switch (status) {
    case NotificationStatus.SUCCESS: {
      return <CheckIcon className={className} {...props} />
    }
    case NotificationStatus.ERROR: {
      return <ExclamationIcon solid className={className} {...props} />
    }
    default: {
      return <InformationIcon className={cx('text-blue-500', className)} {...props} />
    }
  }
}

const notificationStyles = (status) => {
  switch (status) {
    case NotificationStatus.SUCCESS: {
      return 'bg-green-100 text-green-800'
    }
    case NotificationStatus.ERROR: {
      return 'bg-red-100 text-red-700'
    }
    default: {
      return 'bg-blue-100 text-blue-600'
    }
  }
}

const statusStyles = (status) => {
  switch (status) {
    case 'entering':
    case 'entered': {
      return 'translate-y-0'
    }
    case 'exiting':
    case 'exited':
    case 'unmounted': {
      return '-translate-y-full'
    }
    default: {
      return ''
    }
  }
}

const Notification = ({
  onClose,
  className,
  status: statusProp,
  children,
  autoHide = true,
  show,
  withClose = true,
}) => {

  const { useState, useRef, useEffect } = React
  const [open, setOpen] = useState(show)
  const [exited, setExited] = useState(!show)
  const nodeRef = useRef(null)

  useEffect(() => {
    setOpen(show)
    setExited(!show)
  }, [show])

  const handleClose = () => {
    setOpen(false)
    onClose?.()
  }

  const handleOnEnter = () => {
    setExited(false)
  }

  const handleOnExited = () => {
    setExited(true)
  }

  return (
    <Snackbar
      autoHideDuration={autoHide ? 5000 : null}
      open={open}
      onClose={handleClose}
      exited={exited}
      role={statusProp === NotificationStatus.INFORMATION ? 'status' : 'alert'}
    >
      <Transition
        timeout={{ enter: 400, exit: 400 }}
        in={open}
        appear
        unmountOnExit
        onEnter={handleOnEnter}
        onExited={handleOnExited}
        nodeRef={nodeRef}
      >
        {status => (
          <div
            className={cx('transition-transform pt-5 z-20 w-full fixed top-0 left-0', statusStyles(status), className)}
            ref={nodeRef}
          >
            <div
              className={cx(
                'text-s-bold min-h-[56px] rounded elevation-modal flex items-center justify-between p-4 m-auto w-[750px] space-x-6',
                notificationStyles(statusProp),
                statusStyles(status)
              )}
            >
              <Icon status={statusProp} className="w-4 h-4" role="img" />

              <div className="flex-1">{children}</div>
              {withClose && (
                <CloseIcon
                  onClick={handleClose}
                  className={cx(statusProp === NotificationStatus.ERROR ? 'text-red-700' : 'text-jet-black')}
                />
              )}
            </div>
          </div>
        )}
      </Transition>
    </Snackbar>
  )
}

export default Notification
